import { InfoDialogComponent } from '@/components/info-dialog/info-dialog.component';
import { DateTimeFormatPipe } from '@/extensions/dateTimePipe';
import { UserHelper } from '@/helpers/user.helper';
import { Paging } from '@/models/Paging';
import { AlertService } from '@/services/alert.service';
import { NgIf } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import {
  AdminService,
  Event,
  EventLevel,
  EventType,
  RealProperty,
  RealPropertySummary,
  UserSummary
} from '@eemo/api-client';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, merge, of as observableOf } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';

import { InputComponent } from '@/components/ui-forms/input/input.component';
import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { MatExpansionModule } from '@angular/material/expansion';
import { SelectComponent } from '../../../components/ui-forms/select/select.component';

@Component({
  selector: 'app-admin-events',
  templateUrl: './admin-events.component.html',
  styleUrls: ['./admin-events.component.scss'],
  standalone: true,
  imports: [
    MatExpansionModule,
    SelectComponent,
    InputComponent,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    TooltipDirective,
    NgIf,
    MatProgressSpinnerModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    DateTimeFormatPipe
  ]
})
export class AdminEventsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  dataSource: MatTableDataSource<Event>;
  displayedColumns = ['date', 'type', 'level', 'details', 'userId', 'propertyId'];
  resultsLength = 0;
  isLoadingResults = true;

  startDateInvalid = false;
  endDateInvalid = false;
  filterStartDateInput: string;
  filterEndDateInput: string;

  availableTypes: EventType[];
  availableLevels: EventLevel[];

  sub = new Subscription();

  paging = new Paging();

  get form_type() {
    return this.form.get('type') as FormControl<EventType>;
  }
  get form_level() {
    return this.form.get('level') as FormControl<EventLevel>;
  }
  get form_dateStart() {
    return this.form.get('dateStart') as FormControl<string>;
  }
  get form_dateEnd() {
    return this.form.get('dateEnd') as FormControl<string>;
  }
  get form_user() {
    return this.form.get('user') as FormControl<UserSummary>;
  }
  get form_property() {
    return this.form.get('property') as FormControl<RealProperty>;
  }

  form = this.fb.group({
    type: [null],
    level: [null],
    dateStart: [null],
    dateEnd: [null],
    user: [null],
    property: [null]
  });

  constructor(
    private fb: UntypedFormBuilder,
    private adminService: AdminService,
    private alerteService: AlertService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit() {
    this.availableTypes = Object.keys(EventType) as EventType[];
    this.availableLevels = Object.keys(EventLevel) as EventLevel[];
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  ngAfterViewInit() {
    // If the user changes the sort order, reset back to the first page.
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.loadData();
  }

  loadData() {
    this.sub = merge(this.sort.sortChange, this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.isLoadingResults = true;
          return this.adminService.getEventLogs(
            this.form_type.value,
            this.form_level.value,
            this.form_user.value?.uid,
            this.form_property.value?.uid,
            this.form_dateStart.value ? (new Date(this.form_dateStart.value).toISOString() as unknown as Date) : null,
            this.form_dateEnd.value ? (new Date(this.form_dateEnd.value).toISOString() as unknown as Date) : null,
            this.paginator.pageIndex * this.paginator.pageSize,
            this.paginator.pageSize
          );
        }),
        map((dataList) => {
          // Flip flag to show that loading has finished.
          this.isLoadingResults = false;
          this.resultsLength = dataList.paging.totalItems;
          this.alerteService.clear();
          return dataList.list;
        }),
        catchError((error) => {
          this.isLoadingResults = false;
          return observableOf([]);
        })
      )
      .subscribe((data) => {
        const displayedData = [];
        data.forEach((i: Event) => {
          const row = {
            event: i,
            date: i.date,
            level: this.translate.instant('enum.EventLevel.' + i.level),
            type: this.translate.instant('enum.EventType.' + i.type),
            details: i.details,
            stackTrace: i.stackTrace,
            user: i.user ? (i.user.name ? UserHelper.userSummmaryDisplayName(i.user) : i.user.uid) : '',
            property: i.property ? (i.property.name ? i.property.name : i.property.uid) : '',
            cssClass: i.level.toLowerCase()
          };
          displayedData.push(row);
        });
        this.dataSource = new MatTableDataSource(displayedData);
      });
  }

  updateDataFromFilters() {
    this.paginator.pageIndex = 0;
    this.loadData();
  }

  onAddUser(user: UserSummary) {
    this.form_user.setValue(user);
    this.updateDataFromFilters();
  }

  onDeleteUser() {
    this.form_user.setValue(null);
    this.updateDataFromFilters();
  }

  onAddProperty(property: RealPropertySummary) {
    this.form_property.setValue(property);
    this.updateDataFromFilters();
  }

  onDeleteProperty() {
    this.form_property.setValue(null);
    this.updateDataFromFilters();
  }

  showStackTrace(stackTrace: string) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(InfoDialogComponent, {
        maxWidth: '90vw',
        data: {
          title: 'Détail de la stacktrace',
          okButtonText: 'OK',
          content: stackTrace.replace(/\n/g, '<br/>')
        }
      });
    }
  }

  goToProperty(propId) {
    this.router.navigate(['/property/' + propId]);
  }
}
